import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, Clock, RotateCcw, ShieldCheck } from 'lucide-react';
import { AppDispatch } from '../../redux/store';
import { logout } from '../../redux/auth/authSlice';
import Sidebar from '../../components/sidebar/Sidebar';
import { ResponsiveAppShell, TaskyButton } from '../../components/tasky';
import { TaskyHeaderActions } from '../../components/tasky/layout/TaskyHeaderActions';
import { adOperationsPath } from '../../components/taap/TaapTabs';
import TaapProposalBannerIdeas from '../../components/taap/TaapProposalBannerIdeas';
import taapService from '../../services/api/taapService';
import { TaapProposal } from '../../types/taap';
import {
  formatMoney,
  formatNumber,
  PLATFORM_LABELS,
  PRIORITY_LABELS,
  PROPOSAL_TYPE_LABELS,
} from '../../utils/taapFormat';

/**
 * DCM は小さい変更から試す。提案の type がどの階層に当たるかを示して、
 * 「なぜこの手を先に打つのか」を説明する。
 */
const DCM_LEVELS: { key: string; label: string; types: string[] }[] = [
  { key: 'L1', label: '表現（コピー・デザイン）', types: ['creative_refresh'] },
  { key: 'L2', label: '接続（広告→LPの整合）', types: [] },
  { key: 'L3', label: '訴求（軸・タイプ）', types: ['appeal_change', 'experiment'] },
  { key: 'L4-6', label: '価値・WHO・セグメント', types: [] },
];

const TaapProposalDetailPage: React.FC = () => {
  const { proposalId } = useParams<{ proposalId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { search } = useLocation();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [proposal, setProposal] = useState<TaapProposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!proposalId) return;
    setLoading(true);
    try {
      setProposal(await taapService.getProposal(proposalId));
    } catch (e) {
      console.error('TAAP proposal detail load failed:', e);
      setMessage('提案の読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [proposalId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async () => {
    if (!proposal) return;
    setBusy(true);
    try {
      await taapService.approveProposal(proposal.id);
      setMessage('承認しました。');
      await load();
    } catch (e) {
      console.error('TAAP approve failed:', e);
      setMessage('承認に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!proposal) return;
    const reason = window.prompt('却下の理由（任意）') ?? undefined;
    setBusy(true);
    try {
      await taapService.rejectProposal(proposal.id, reason);
      setMessage('却下しました。');
      await load();
    } catch (e) {
      console.error('TAAP reject failed:', e);
      setMessage('却下に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const sidebar = (
    <Sidebar onLogout={handleLogout} onToggleCollapse={setSidebarCollapsed} collapsed={sidebarCollapsed} />
  );

  const target = proposal
    ? [
        proposal.target.platform ? PLATFORM_LABELS[proposal.target.platform] ?? proposal.target.platform : null,
        proposal.target.campaign_name,
        proposal.target.adset_name,
        proposal.target.creative_name,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <ResponsiveAppShell
      sidebar={sidebar}
      mobileSidebar={<Sidebar onLogout={handleLogout} />}
      topbarTitle="提案詳細"
    >
      <div className="flex min-h-[calc(100dvh_-_56px)] bg-warm-bg lg:h-screen lg:min-h-0">
        <div className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
          <div className="mx-auto w-full max-w-[1400px] p-6 max-[640px]:p-4">
            <div className="mb-4 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => navigate(adOperationsPath('/taap/proposals', search))}
                className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-4 w-4" />
                提案・承認に戻る
              </button>
              <span className="hidden items-center lg:flex"><TaskyHeaderActions /></span>
            </div>

            {loading ? (
              <div className="rounded-xl border border-gray-200 bg-white px-5 py-10 text-center text-sm text-gray-500">
                読み込み中…
              </div>
            ) : !proposal ? (
              <div className="rounded-xl border border-gray-200 bg-white px-5 py-10 text-center text-sm text-gray-500">
                提案が見つかりませんでした。
              </div>
            ) : (
              <>
                <TaapProposalBannerIdeas proposal={proposal} />
                <div className="rounded-xl border border-gray-200 bg-white p-5" data-tutorial="taap-proposal-detail-summary">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded-md border border-red-200 bg-red-50 px-2 py-0.5 font-medium text-red-700">
                      {PRIORITY_LABELS[proposal.priority] ?? proposal.priority}
                    </span>
                    <span className="text-gray-600">{PROPOSAL_TYPE_LABELS[proposal.type] ?? proposal.type}</span>
                    <span className="flex items-center gap-1 text-gray-500">
                      <Clock className="h-3 w-3" />
                      実行 約{proposal.estimated_minutes}分
                    </span>
                    {proposal.reversible && (
                      <span className="flex items-center gap-1 text-emerald-700">
                        <RotateCcw className="h-3 w-3" />
                        復元できます
                      </span>
                    )}
                  </div>

                  <h1 className="mt-2 text-lg font-bold text-gray-900">{proposal.title}</h1>
                  {target && <div className="mt-1 text-xs text-gray-500">対象: {target}</div>}
                  <p className="mt-3 text-sm leading-relaxed text-gray-700">{proposal.description}</p>
                </div>

                {/* 診断の根拠 */}
                <div className="mt-4 rounded-xl border border-gray-200 bg-white p-5">
                  <h2 className="text-sm font-semibold text-gray-900">
                    診断の根拠 — {proposal.evidence?.length ?? 0}つのシグナルが揃いました
                  </h2>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {(proposal.evidence ?? []).map((signal) => (
                      <div key={signal.key} className="rounded-lg border border-gray-200 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">{signal.label}</span>
                          <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                            シグナル点灯
                          </span>
                        </div>
                        <div className="mt-1.5 text-lg font-bold tabular-nums text-gray-900">{signal.display}</div>
                        <div className="mt-0.5 text-xs text-gray-500">{signal.threshold_display}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 影響サマリー */}
                <div className="mt-4 rounded-xl border border-gray-200 bg-white p-5">
                  <h2 className="text-sm font-semibold text-gray-900">影響サマリー</h2>
                  <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {proposal.expected_effect?.cpa_change_rate !== undefined && (
                      <div>
                        <div className="text-xs text-gray-500">推定CPA改善</div>
                        <div className="mt-0.5 text-base font-bold text-emerald-700">
                          {proposal.expected_effect.cpa_change_rate.toFixed(0)}%
                        </div>
                      </div>
                    )}
                    {proposal.expected_effect?.monthly_saving !== undefined && (
                      <div>
                        <div className="text-xs text-gray-500">月あたり改善見込み</div>
                        <div className="mt-0.5 text-base font-bold text-emerald-700">
                          {formatMoney(proposal.expected_effect.monthly_saving)}
                        </div>
                      </div>
                    )}
                    {proposal.expected_effect?.conversions_delta !== undefined && (
                      <div>
                        <div className="text-xs text-gray-500">CV増加見込み</div>
                        <div className="mt-0.5 text-base font-bold text-emerald-700">
                          +{formatNumber(proposal.expected_effect.conversions_delta)}件/月
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-xs text-gray-500">リスク</div>
                      <div className="mt-0.5 text-base font-bold text-gray-900">
                        {proposal.reversible ? '低（後戻り可能）' : '中'}
                      </div>
                    </div>
                  </div>

                  {proposal.change_summary && (
                    <div className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-700">
                      <span className="font-medium">変更内容: </span>
                      {proposal.change_summary}
                    </div>
                  )}
                </div>

                {/* 変更の階層 */}
                <div className="mt-4 rounded-xl border border-gray-200 bg-white p-5">
                  <h2 className="text-sm font-semibold text-gray-900">変更の階層</h2>
                  <div className="mt-3 space-y-1.5">
                    {DCM_LEVELS.map((level) => {
                      const isCurrent = level.types.includes(proposal.type);

                      return (
                        <div
                          key={level.key}
                          className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-sm ${
                            isCurrent ? 'border-primary/40 bg-primary/5' : 'border-gray-200'
                          }`}
                        >
                          <span
                            className={`w-12 shrink-0 text-xs font-semibold ${
                              isCurrent ? 'text-primary' : 'text-gray-400'
                            }`}
                          >
                            {level.key}
                          </span>
                          <span className={isCurrent ? 'text-gray-900' : 'text-gray-500'}>{level.label}</span>
                          {isCurrent && <Check className="ml-auto h-4 w-4 text-primary" />}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {message && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700">
                    {message}
                  </div>
                )}

                {proposal.status === 'pending' ? (
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <TaskyButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={handleReject}
                      className="border border-warm-border bg-white"
                    >
                      却下
                    </TaskyButton>
                    <TaskyButton
                      type="button"
                      size="sm"
                      disabled={busy}
                      onClick={handleApprove}
                      className="ml-auto"
                    >
                      <ShieldCheck />
                      提案を承認
                    </TaskyButton>
                  </div>
                ) : (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-600">
                    この提案は
                    {proposal.status === 'rejected'
                      ? '却下済みです。'
                      : proposal.status === 'measured'
                        ? '実行・計測まで完了しています。'
                        : '承認済みです。'}
                    {proposal.rejected_reason && (
                      <span className="mt-1 block text-xs text-gray-500">却下理由: {proposal.rejected_reason}</span>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default TaapProposalDetailPage;
